import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, Loader2, Search, Check, AlertTriangle } from 'lucide-react';
import { api } from '../../api/client';
import { Checkbox } from '../ui/checkbox';
import { normalizeBulkResult, triggerOptionsFor } from '../../lib/maintenanceTriggers';
import { useLocale } from '../../contexts/LocaleContext';
import {
  ScheduleFormFields,
  emptyScheduleForm,
  scheduleFormError,
  scheduleFormPayload,
  type ScheduleFormState,
} from './ScheduleFormFields';
import type {
  AssetKind,
  BulkScheduleResult,
  MaintenanceSchedule,
} from '../../types/equipment';

interface Unit {
  id: string;
  label: string;
  type: string | null;
  assetKind: string;
}

interface BulkApplySchedulesDialogProps {
  open: boolean;
  onClose: () => void;
  /** The unit the section belongs to — used to scope the candidate list by asset kind. */
  equipmentId: string;
  assetKind: AssetKind | string | null | undefined;
  /** Optional schedule to pre-fill the form from (the "apply this one elsewhere" path). */
  template?: MaintenanceSchedule | null;
}

export default function BulkApplySchedulesDialog({
  open,
  onClose,
  equipmentId,
  assetKind,
  template,
}: BulkApplySchedulesDialogProps) {
  const queryClient = useQueryClient();
  const { isMetric } = useLocale();

  const options = useMemo(() => triggerOptionsFor(assetKind, isMetric), [assetKind, isMetric]);
  const [form, setForm] = useState<ScheduleFormState>(emptyScheduleForm());
  const [selected, setSelected] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const [result, setResult] = useState<BulkScheduleResult | null>(null);

  const { data: unitsData, isLoading: unitsLoading } = useQuery({
    queryKey: ['equipment'],
    queryFn: () =>
      api.equipment.list().then((r) => {
        const d = r.data?.data ?? r.data;
        return Array.isArray(d) ? d : [];
      }),
    staleTime: 60_000,
    enabled: open,
  });

  const units: Unit[] = useMemo(
    () =>
      (unitsData ?? []).map((e: any) => ({
        id: e.id,
        label: e.unitNumber || e.unit_number || e.name || e.serialNumber || e.serial_number || 'Unnamed unit',
        type: e.type ?? null,
        assetKind: e.assetKind ?? e.asset_kind ?? 'equipment',
      })),
    [unitsData],
  );

  /**
   * Only units of the same asset kind are eligible — an odometer schedule on a
   * power tong (or a job_count schedule on a truck) is not a valid trigger for
   * that kind.
   */
  const eligible = useMemo(() => {
    const wantVehicle = assetKind === 'vehicle';
    return units.filter((u) => (u.assetKind === 'vehicle') === wantVehicle);
  }, [units, assetKind]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return eligible;
    return eligible.filter(
      (u) => u.label.toLowerCase().includes(q) || (u.type ?? '').toLowerCase().includes(q),
    );
  }, [eligible, filter]);

  const nameFor = (id: string) => units.find((u) => u.id === id)?.label ?? `Unit ${id.slice(0, 8)}`;

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setFilter('');
    setSelected([]);
    setForm(
      template
        ? {
            name: template.name ?? '',
            trigger_type: template.triggerType,
            interval_value: template.intervalValue != null ? String(template.intervalValue) : '',
            last_performed_at: '',
            last_performed_value: '',
          }
        : emptyScheduleForm(),
    );
  }, [open, template]);

  const bulkMutation = useMutation({
    mutationFn: () =>
      api.equipmentMaintenanceSchedules.bulk({
        ...scheduleFormPayload(form),
        equipment_ids: selected,
      }),
    onSuccess: (res) => {
      const normalized = normalizeBulkResult(res.data, selected, nameFor);
      setResult(normalized);
      queryClient.invalidateQueries({ queryKey: ['equipment-maintenance-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-maintenance-due'] });
      if (normalized.createdCount > 0) {
        toast.success(
          `Created ${normalized.createdCount} schedule${normalized.createdCount === 1 ? '' : 's'}` +
            (normalized.skippedCount > 0 ? `, skipped ${normalized.skippedCount}` : ''),
        );
      } else {
        toast.error(`No schedules created — ${normalized.skippedCount} unit(s) skipped`);
      }
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      const message = err?.response?.data?.error;
      toast.error(
        status === 404
          ? 'Bulk apply is not available on this API build (endpoint returned 404).'
          : message || 'Bulk apply failed',
      );
    },
  });

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const allVisibleSelected = visible.length > 0 && visible.every((u) => selected.includes(u.id));
  const toggleAllVisible = () =>
    setSelected((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visible.some((u) => u.id === id))
        : Array.from(new Set([...prev, ...visible.map((u) => u.id)])),
    );

  const handleSubmit = () => {
    const error = scheduleFormError(form);
    if (error) {
      toast.error(error);
      return;
    }
    if (selected.length === 0) {
      toast.error('Select at least one unit');
      return;
    }
    bulkMutation.mutate();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 overflow-y-auto py-8">
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-3xl mx-4 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-fieldiq-border">
          <div>
            <h3 className="text-lg font-bold text-white">Apply Schedule to Multiple Units</h3>
            <p className="text-xs text-fieldiq-text mt-0.5">
              {assetKind === 'vehicle' ? 'Vehicles' : 'Equipment'} only — triggers differ by asset
              kind.
            </p>
          </div>
          <button onClick={onClose} className="text-fieldiq-muted hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {result ? (
          /* ── Result view ── */
          <div className="px-6 py-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-fieldiq-dark rounded-lg border border-fieldiq-border p-4">
                <p className="text-xs text-fieldiq-muted">Created</p>
                <p className="text-2xl font-bold text-emerald-400 mt-0.5">{result.createdCount}</p>
              </div>
              <div className="bg-fieldiq-dark rounded-lg border border-fieldiq-border p-4">
                <p className="text-xs text-fieldiq-muted">Skipped</p>
                <p
                  className={`text-2xl font-bold mt-0.5 ${
                    result.skippedCount > 0 ? 'text-amber-400' : 'text-white'
                  }`}
                >
                  {result.skippedCount}
                </p>
              </div>
            </div>

            {result.skippedCount > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={16} className="text-amber-400" />
                  <p className="text-sm font-semibold text-amber-400">Skipped units</p>
                </div>
                {result.skipped.length > 0 ? (
                  <ul className="space-y-1.5">
                    {result.skipped.map((unit, i) => (
                      <li key={unit.equipmentId ?? i} className="text-xs">
                        <span className="text-white font-medium">{unit.name}</span>
                        {unit.reason && <span className="text-fieldiq-text"> — {unit.reason}</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-fieldiq-text">
                    The API reported {result.skippedCount} skipped unit
                    {result.skippedCount === 1 ? '' : 's'} but did not name them. Most often this
                    means the unit already has a schedule with this name.
                  </p>
                )}
              </div>
            )}

            {result.createdCount > 0 && result.skippedCount === 0 && (
              <div className="flex items-center gap-2 text-sm text-emerald-400">
                <Check size={16} /> Applied to every selected unit.
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-1">
              <button
                onClick={() => setResult(null)}
                className="px-4 py-2 border border-fieldiq-border text-fieldiq-muted rounded-lg text-sm font-medium hover:text-white transition-colors"
              >
                Apply Another
              </button>
              <button
                onClick={onClose}
                className="bg-fieldiq-accent text-fieldiq-dark font-semibold px-5 py-2 rounded-lg text-sm hover:bg-amber-400 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-5 max-h-[65vh] overflow-y-auto">
              {/* Schedule definition */}
              <div className="space-y-4">
                <p className="text-[10px] uppercase tracking-wider text-fieldiq-muted font-semibold">
                  Schedule
                </p>
                <ScheduleFormFields
                  form={form}
                  onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
                  options={options}
                />
              </div>

              {/* Unit picker */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-wider text-fieldiq-muted font-semibold">
                    Units ({selected.length} selected)
                  </p>
                  {visible.length > 0 && (
                    <button
                      onClick={toggleAllVisible}
                      className="text-xs text-fieldiq-accent hover:text-amber-300 font-medium"
                    >
                      {allVisibleSelected ? 'Clear all' : 'Select all'}
                    </button>
                  )}
                </div>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fieldiq-muted" />
                  <input
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter units..."
                    className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg pl-9 pr-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-amber-500/50"
                  />
                </div>

                <div className="border border-fieldiq-border rounded-lg divide-y divide-fieldiq-border max-h-72 overflow-y-auto">
                  {unitsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-fieldiq-accent" />
                    </div>
                  ) : visible.length === 0 ? (
                    <p className="text-xs text-fieldiq-muted text-center py-8">
                      No matching {assetKind === 'vehicle' ? 'vehicles' : 'equipment'}.
                    </p>
                  ) : (
                    visible.map((unit) => (
                      <label
                        key={unit.id}
                        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-fieldiq-dark/50 transition-colors"
                      >
                        <Checkbox
                          checked={selected.includes(unit.id)}
                          onCheckedChange={() => toggle(unit.id)}
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm text-white truncate">
                            {unit.label}
                            {unit.id === equipmentId && (
                              <span className="text-fieldiq-muted"> (this unit)</span>
                            )}
                          </span>
                          {unit.type && (
                            <span className="block text-[11px] text-fieldiq-muted truncate">
                              {unit.type}
                            </span>
                          )}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-fieldiq-border">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-fieldiq-border text-fieldiq-muted rounded-lg text-sm font-medium hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={bulkMutation.isPending}
                className="flex items-center gap-2 bg-fieldiq-accent text-fieldiq-dark font-semibold px-5 py-2 rounded-lg text-sm hover:bg-amber-400 transition-colors disabled:opacity-50"
              >
                {bulkMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Apply to {selected.length} Unit{selected.length === 1 ? '' : 's'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
